<template>
  <div>
    <ValidationObserver
      ref="formValidation"
      v-slot="{ invalid }"
      slim
    >
      <form
        role="form"
        class="form-horizontal"
        name="integrationFrom"
        novalidate
      >
        <fieldset>
          <Field
            v-if="integration || currentIntegration.id"
            :label="$t('PLATFORM_NAME_LABEL')"
            :has-tooltip="true"
            :tooltip-text="$t('PLATFORM_NAME_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="name"
                rules="required"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4">
                    <input
                      data-testid="integration-name"
                      v-model="currentIntegration.name"
                      type="text"
                      :placeholder="$t('PLATFORM_NAME_PH')"
                      name="name"
                      autocomplete="off"
                      class="form-control"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>
          <Field
            v-if="!integration && !newIntegration"
            :label="$t('PLATFORM_TYPE_LABEL')"
            :has-tooltip="true"
            :tooltip-text="$t('PLATFORM_TYPE_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="type"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4 col-md-3">
                    <SearchableDropdown
                      data-testid="type-dropdown"
                      v-model="currentIntegration.type"
                      :config="integrationOptions"
                      @change="onIntegrationTypeChange"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>
          <Field
            v-if="(!integration || showOauthRegisterLoader) && !newIntegration"
            :label="$t('PLATFORM_AUTHORIZE_LABEL')"
            :has-tooltip="true"
            :tooltip-text="$t('PLATFORM_AUTHORIZE_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="subId"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4">
                    <img
                      v-if="!showOauthRegisterLoader && (currentIntegration.type == 'googleAds')"
                      data-testid="google-auth-button"
                      class="clickable width-auto"
                      src="@/assets/images/btn_google_signin_light_normal_web.png"
                      @click="authorize"
                    />
                    <button
                      v-if="!showOauthRegisterLoader && currentIntegration.type != 'googleAds'"
                      data-testid="other-auth-button"
                      class="btn btn-success btn-sm"
                      v-t="'PLATFORM_AUTHORIZE_BTN'"
                      type="button"
                      @click="authorize"
                    />
                    <p
                      v-if="showOauthRegisterLoader"
                      class="form-control-static m-b-4"
                    >
                      <Loader custom-class="loader-container size-sm" />
                      <span v-t="'REGISTERING'" />
                    </p>
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>
          <Field
            v-if="currentIntegration.type.toLowerCase() == 'googleads' && (newIntegration || (integration && integration.status === 'Pending'))"
            :label="$t('PLATFORM_ACCOUNT_LABEL')"
            :has-tooltip="true"
            :tooltip-text="$t('PLATFORM_ACCOUNT_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="platformPrimaryId"
                rules="required"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4">
                    <p
                      v-if="loadingAccountsForUser"
                      class="form-control-static m-b-4"
                    >
                      <Loader custom-class="loader-container size-sm" />
                      <span v-t="'LOADING_ACCOUNTS'" />
                    </p>
                    <SearchableDropdown
                      v-else
                      data-testid="account-dropdown"
                      name="platformPrimaryId"
                      v-model="currentIntegration.platformPrimaryId"
                      :config="integrationAccountsOptions"
                      :class="classes"
                      :rules="'required'"
                      @change="onAccountSelect"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>
          <Field
            v-if="showSubAccounts"
            :label="$t('PLATFORM_SUB_MCC_ACCOUNT_LABEL')"
            :has-tooltip="true"
            :tooltip-text="$t('PLATFORM_SUB_MCC_ACCOUNT_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="subAccountId"
                rules="required"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4">
                    <p
                      v-if="loadingSubAccountsForUser"
                      class="form-control-static m-b-4"
                    >
                      <Loader custom-class="loader-container size-sm" />
                      <span v-t="'LOADING_SUB_ACCOUNTS'" />
                    </p>
                    <SearchableDropdown
                      v-else
                      data-testid="sub-account-dropdown"
                      name="subAccountId"
                      v-model="currentIntegration.subAccountId"
                      :config="integrationSubAccountsOptions"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>

          <Field
            v-if="isFacebookIntegration && (newIntegration || (integration && integration.status === 'Pending'))"
            :label="$t('PLATFORM_BUSINESS_MANAGER_LABEL')"
            :has-tooltip="true"
            :tooltip-text="$t('PLATFORM_BUSINESS_MANAGER_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="platformPrimaryId"
                rules="required"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4">
                    <p
                      v-if="loadingFacebookDetailsForUser"
                      class="form-control-static m-b-4"
                    >
                      <Loader custom-class="loader-container size-sm" />
                      <span v-t="'LOADING_BUSINESS_MANAGER_LIST'" />
                    </p>
                    <SearchableDropdown
                      v-else
                      data-testid="business-manager-dropdown"
                      name="platformPrimaryId"
                      v-model="currentIntegration.platformPrimaryId"
                      :config="integrationBusinessManagerOptions"
                      @change="onBusinessAccountSelect"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>

          <Field
            v-if="showFbAdAccount"
            :label="$t('PLATFORM_AD_ACCOUNT_LABEL')"
            :has-tooltip="true"
            :tooltip-text="$t('PLATFORM_AD_ACCOUNT_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="platformSubId"
                rules="required"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4">
                    <SearchableDropdown
                      data-testid="fb-ad-accounts-dropdown"
                      name="platformSubId"
                      v-model="currentIntegration.platformSubId"
                      :config="integrationAdAccountsOptions"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>

          <div class="form-group">
            <div class="col-sm-8 col-sm-offset-3">
              <PrimaryButton
                v-if="integration || newIntegration"
                data-testid="submit-button"
                size="sm"
                type="button"
                class="m-r-15"
                :show-loader-on-form="isUpdating"
                :disabled="showOauthRegisterLoader || loadingAccountsForUser || invalid || isUpdating"
                @click="onSave"
              >
                <span v-t="'UPDATE'" />
              </PrimaryButton>
              <button
                data-testid="cancel-button"
                v-t="'CANCEL'"
                type="button"
                class="btn btn-sm btn-danger"
                :disabled="showOauthRegisterLoader || loadingAccountsForUser || isUpdating"
                @click="closeForm"
              />
            </div>
          </div>
        </fieldset>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { utilities, integrationService, oauthService, accountsService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { isStaging } from '@/core/utilities';
import { mapActions, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import { cloneDeep } from 'lodash';
import messages from '../../locales';
import Field from '@/core/components/LabelAndElement/LabelAndElement.vue';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';

const integrationTypes = [
  {
    label: 'Google Ads',
    value: 'googleAds'
  },
  {
    label: 'Facebook (Legacy)',
    value: 'facebook'
  },
  {
    label: 'Facebook Events',
    value: 'facebookEvents'
  }
];

export default {
  name: 'PlatformForm',
  components: {
    Field,
    SearchableDropdown
  },
  props: {
    integration: {
      type: Object,
      default: null,
      validator(value) {
        if (!value) return true;
        return ['id', 'name', 'type'].every(prop => prop in value);
      }
    },
  },
  data() {
    return {
      currentIntegration: {
        name: '',
        type: integrationTypes[0].value
      },
      showOauthRegisterLoader: false,
      authUrl: '',
      oauthCallbackWindow: null,
      newIntegration: false,
      loadingAccountsForUser: false,
      loadingFacebookDetailsForUser: false,
      integrationAccounts: [],
      showSubAccounts: false,
      loadingSubAccountsForUser: false,
      adwordsMCCSubAccounts: [],
      facebookBusinesses: [],
      facebookAdAccounts: []
    };
  }, 
  created() {
    if (this.integration) {
      this.currentIntegration = cloneDeep(this.integration);
    }
    this._getAuthUrl();

    if (this.currentIntegration.id && this.currentIntegration.status === 'Pending') {
      this._loadNextStepAdAccountDetails(this.currentIntegration);
    }
  },
  computed: {
    ...mapGetters('platforms', [
      'updateIntegrationStatus',
    ]),
    isUpdating() {
      return this.updateIntegrationStatus.status == STATUS_PENDING;
    },
    isFacebookIntegration() {
      return this.currentIntegration.type === 'facebook' || this.currentIntegration.type === 'facebookEvents';
    },
    showFbAdAccount() {
      // The Facebook ad account selection is only shown for the legacy version
      return this.currentIntegration?.type === 'facebook' && (this.newIntegration || (this.integration && this.integration.status === 'Pending')) && this.facebookAdAccounts?.length ? true : false;
    },
    integrationOptions() {
      return {
        options: integrationTypes,
        ...{
          reduce: (option) => option.value,
          label: 'label'
        },
        placeholder: this.$t('PLATFORM_TYPE_PH')
      };
    },
    integrationAccountsOptions() {
      return {
        options: this.integrationAccounts.map((option) => ({
          ...option,
          label: option.name ? `${option.name} - ${option.id}` : option.id,
        })),
        reduce: (option) => option.id,
        label: 'label',
        placeholder: this.$t('PLATFORM_ACCOUNT_PH')
      };
    },
    integrationSubAccountsOptions() {
      return {
        options: this.adwordsMCCSubAccounts,
        ...{
          reduce: (option) => option.id,
          label: 'name'
        },
        placeholder: this.$t('PLATFORM_SUB_MCC_ACCOUNT_PH')
      };
    },
    integrationBusinessManagerOptions() {
      return {
        options: this.facebookBusinesses.map((option) => ({
          ...option,
          label: option.name ? `${option.name} - ${option.id}` : option.id,
        })),
        ...{
          reduce: (option) => option.id,
          label: 'name'
        },
        placeholder: this.$t('PLATFORM_BUSINESS_MANAGER_PH')
      };
    },
    integrationAdAccountsOptions() {
      return {
        options: this.facebookAdAccounts,
        ...{
          reduce: (option) => option.account_id,
          label: 'name'
        },
        placeholder: this.$t('PLATFORM_AD_ACCOUNT_PH')
      };
    }
  }, 
  beforeDestroy() {
    this._UnregisterOAuthMessageHandler();
  },
  methods: {
    ...mapActions('platforms', [
      'updateIntegration'
    ]),
    onIntegrationTypeChange() {
      this._getAuthUrl();
    },
    async authorize() {
      this._RegisterOAuthMessageHandler();
      if (isStaging) {
        const response = await integrationService.getOauthMessageEvent();
        const ev = {source: null, ...response}
        this._receiveOAuthMessage(ev);
        return;
      }
      this.oauthCallbackWindow = window.open(this.authUrl);
    },
    async onSave() {
      await this.updateIntegration(this.currentIntegration);
      if (this.updateIntegrationStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t('PLATFORM_INTEGRATION_SUCCESS_UPDATE'));
        if (this.newIntegration) {
          this.closeForm();
        }
      }

      if (this.updateIntegrationStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.updateIntegrationStatus.error);
      }
    },
    async closeForm() {
      this.$emit('closeForm', this.newIntegration);
    },
    oauthWindowErrorCallback(error) {
      utilities.errorHandler(error.message);
      _UnregisterOAuthMessageHandler();
    },
    async oauthWindowSuccessCallback(authCode) {
      this.showOauthRegisterLoader = true;
      const integrationAuthType = integrationService.getIntegrationAuthType(this.currentIntegration.type);
      try {
        const response = await oauthService.saveOauthData(accountsService.currentAccountId, integrationAuthType, { code: authCode, type: this.currentIntegration.type });
        this.currentIntegration = cloneDeep(response.data.integration);
        this.newIntegration = true;
        utilities.showToaster(STATUS_SUCCESS, 'Thank you for authorizing ' + integrationService.getPlatformLabel(this.currentIntegration.type) + ' access.');
        this._loadNextStepAdAccountDetails(this.currentIntegration);
      } catch (error) {
          utilities.errorHandler(error);
      } finally {
        this.showOauthRegisterLoader = false;
        this._UnregisterOAuthMessageHandler();
      }
    },
    onAccountSelect() {
      this.showSubAccounts = false;
      this.currentIntegration.subAccountId = null;
      this.currentIntegration.canManageClients = false;

      if (this.currentIntegration.type.toLowerCase() == 'googleads') {
        let fullAccountData = this.integrationAccounts.find(x => x.id === this.currentIntegration.platformPrimaryId);
        if (fullAccountData) {
          this.currentIntegration.platformSubId = fullAccountData.managerId;  
        }
        if (fullAccountData.canManageClients) {
          this.showSubAccounts = true;
          this.currentIntegration.canManageClients = true;
          this._loadMCCSubAccounts(this.currentIntegration, fullAccountData.id);
        }
      }
    },
    onBusinessAccountSelect(business) {
      this.currentIntegration = this.currentIntegration || {};
      this.currentIntegration.platformSubId = undefined;
      const selectedBusiness = this.facebookBusinesses.find(option => option.id === business);
      this.facebookAdAccounts = selectedBusiness?.accounts || [];
      // If the integration is Facebook Events, arbitrarily set the first ad account as the platformSubId or set the generic
      if (this.currentIntegration.type === 'facebookEvents') {
        this.currentIntegration.platformSubId = this.facebookAdAccounts[0]?.account_id || 'subId';
      }
    },
    _RegisterOAuthMessageHandler() {
      window.addEventListener('message', this._receiveOAuthMessage, false);
    },
    _UnregisterOAuthMessageHandler() {
      window.removeEventListener('message', this._receiveOAuthMessage, false);
      this.oauthCallbackWindow = null;
    },
    _receiveOAuthMessage(ev) {
      const allowedOrigins = [
        'https://app.ringba.com',
        'https://offers.ringba.com',
        'http://localhost:9000'
      ];

      if ((!isStaging && !allowedOrigins.includes(ev.origin)) || ev.source !== this.oauthCallbackWindow) {
        return;
      }
      
      const msg = ev.data;
      
      if (!msg.success) {
        return this.oauthWindowErrorCallback(msg.error);
      }

      return this.oauthWindowSuccessCallback(msg.authCode);
    },
    async _getAuthUrl() {
      const integrationAuthType = integrationService.getIntegrationAuthType(this.currentIntegration.type);
      const response = await oauthService.getAuthUrl(accountsService.currentAccountId, integrationAuthType);
      this.authUrl = response.data.authUrl;
    },
    _loadNextStepAdAccountDetails(integration) {
      switch (integration.type.toLowerCase()) {
      case 'googleads':
        this._loadAccountsForIntegration(integration);
        break;
      case 'facebook':
      case 'facebookevents':
        this._loadBusinessManagerAndAccounts(integration);
        break;
      }
    },
    async _loadAccountsForIntegration(integration) {
      this.loadingAccountsForUser = true;
      try {
        const response = await integrationService.getIntegrationAccounts(accountsService.currentAccountId, integration);
        this.integrationAccounts = response.data.accounts;
      } catch (error) {
        utilities.errorHandler(error);
      } finally {
        this.loadingAccountsForUser = false;
      }
    },
    async _loadMCCSubAccounts(integration, mccClientCustomerId) {
      this.loadingSubAccountsForUser = true;

      try {
        const response = await integrationService.getIntegrationAccounts(accountsService.currentAccountId, integration, mccClientCustomerId);
        this.adwordsMCCSubAccounts = response.data.accounts.filter(function filterSubAccounts(account) {
          return !account.canManageClients;
        });
      } catch (error) {
        utilities.errorHandler(error);
      } finally {
        this.loadingSubAccountsForUser = false;
      }
    },
    async _loadBusinessManagerAndAccounts(integration) {
      this.loadingFacebookDetailsForUser = true;
      try {
        let response = await integrationService.getFacebookIntegrationDetails(accountsService.currentAccountId, integration);
        this.facebookBusinesses = response.data.businesses;
        // If the integration was partially set up with a business selected, show applicable ad accounts
        if (integration.platformPrimaryId && this.isFacebookIntegration) {
          this.onBusinessAccountSelect(integration.platformPrimaryId);
        }
      } catch (error) {
        utilities.errorHandler(error);
      } finally {
        this.loadingFacebookDetailsForUser = false;
      }
    }
  },  
  i18n: {
    messages,
    locale: 'en'
  }
};
</script>